/**
 * Whistleblower Portal & Arbitration Panel
 * File claims, manage defenses, arbitration council, verdict history
 * Asymmetric justice: filer stakes X, risks 1.5X if dismissed.
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Clock, Eye,
  FileText, Filter, Gavel, Loader2, Plus, RefreshCw,
  Scale, Search, Shield, ShieldAlert,
} from 'lucide-react';
import { fileClaim, getClaims, getClaimDetail } from '@/lib/v2Api';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

const statusConfig = {
  pending: { label: 'Pending', variant: 'outline', color: 'text-amber-500 border-amber-500/30' },
  arbitration_pending: { label: 'Awaiting Arbitration', variant: 'outline', color: 'text-blue-500 border-blue-500/30' },
  arbitration_active: { label: 'In Arbitration', variant: 'outline', color: 'text-violet-500 border-violet-500/30' },
  ai_review: { label: 'AI Review', variant: 'outline', color: 'text-teal-500 border-teal-500/30' },
  resolved_upheld: { label: 'Upheld', variant: 'outline', color: 'text-emerald-500 border-emerald-500/30' },
  resolved_dismissed: { label: 'Dismissed', variant: 'outline', color: 'text-red-500 border-red-500/30' },
  resolved_partially_upheld: { label: 'Partially Upheld', variant: 'outline', color: 'text-amber-500 border-amber-500/30' },
};

const categoryLabels = {
  safety_violation: 'Safety Violation',
  financial_fraud: 'Financial Fraud',
  guest_mistreatment: 'Guest Mistreatment',
  data_manipulation: 'Data Manipulation',
  staff_exploitation: 'Staff Exploitation',
  environmental: 'Environmental Violation',
  other: 'Other',
};

// ─── Claim Card ───
const ClaimCard = ({ claim, onView }) => {
  const sc = statusConfig[claim.status] || statusConfig.pending;
  return (
    <div
      className="p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors cursor-pointer"
      onClick={onView}
      data-testid={`claim-${claim.claim_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] font-mono">{claim.claim_id}</Badge>
          <Badge variant={sc.variant} className={`text-[10px] ${sc.color}`}>{sc.label}</Badge>
        </div>
        <span className="text-[10px] text-muted-foreground">{new Date(claim.created_at).toLocaleDateString()}</span>
      </div>
      <p className="text-sm font-medium mb-1 capitalize">{categoryLabels[claim.category] || claim.category}</p>
      <p className="text-xs text-muted-foreground line-clamp-2">{claim.description}</p>
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span>Filer: {claim.filer_id}</span>
        <span>Target: {claim.target_id}</span>
        {claim.stake_amount > 0 && (
          <span className="text-amber-500">Stake: {fmt(claim.stake_amount)} (Risk: {fmt(claim.risk_if_dismissed)})</span>
        )}
      </div>
    </div>
  );
};

// ─── Claim Detail Dialog ───
const ClaimDetailDialog = ({ open, onClose, detail }) => {
  if (!detail) return null;
  const { claim, defense, verdict } = detail;
  const sc = claim ? (statusConfig[claim.status] || statusConfig.pending) : statusConfig.pending;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="claim-detail-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Claim Details
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{claim?.claim_id}</DialogDescription>
        </DialogHeader>

        {claim && (
          <div className="space-y-4">
            {/* Claim Info */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Badge variant={sc.variant} className={sc.color}>{sc.label}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(claim.created_at).toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Category:</span> <span className="capitalize ml-1">{categoryLabels[claim.category] || claim.category}</span></div>
                <div><span className="text-muted-foreground">Hotel:</span> <span className="ml-1">{claim.hotel_id}</span></div>
                <div><span className="text-muted-foreground">Filer:</span> <span className="ml-1">{claim.filer_id} ({claim.filer_type})</span></div>
                <div><span className="text-muted-foreground">Target:</span> <span className="ml-1">{claim.target_id} ({claim.target_type})</span></div>
              </div>
              <p className="text-sm mt-2">{claim.description}</p>
              {claim.evidence?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase">Evidence</p>
                  {claim.evidence.map((e, i) => (
                    <p key={i} className="text-xs bg-muted/50 px-2 py-1 rounded">{e}</p>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-4 text-sm">
                <span>Stake: <strong>{fmt(claim.stake_amount)}</strong></span>
                <span className="text-red-400">Risk if dismissed: <strong>{fmt(claim.risk_if_dismissed)}</strong></span>
              </div>
            </div>

            {/* Defense */}
            {defense && (
              <>
                <Separator />
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <Shield className="h-3 w-3" /> Defense
                  </p>
                  <p className="text-sm">{defense.defense_statement}</p>
                  {defense.evidence?.length > 0 && (
                    <div className="space-y-1">
                      {defense.evidence.map((e, i) => (
                        <p key={i} className="text-xs bg-muted/50 px-2 py-1 rounded">{e}</p>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Arbitration requested: {defense.requests_arbitration ? 'Yes' : 'No'}
                  </p>
                </div>
              </>
            )}

            {/* Verdict */}
            {verdict && (
              <>
                <Separator />
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                    <Gavel className="h-3 w-3" /> Verdict
                  </p>
                  <Badge className={
                    verdict.verdict === 'upheld' ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' :
                    verdict.verdict === 'dismissed' ? 'bg-red-500/15 text-red-500 border-red-500/30' :
                    'bg-amber-500/15 text-amber-500 border-amber-500/30'
                  }>
                    {verdict.verdict.replace('_', ' ').toUpperCase()}
                  </Badge>
                  <p className="text-sm">{verdict.reasoning}</p>
                  {verdict.reward_to_filer > 0 && (
                    <p className="text-sm text-emerald-500">Filer rewarded: {fmt(verdict.reward_to_filer)} HERMES</p>
                  )}
                  {verdict.filer_penalty > 0 && (
                    <p className="text-sm text-red-500">Filer penalized: {fmt(verdict.filer_penalty)} HERMES</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── File Claim Dialog ───
const FileClaimDialog = ({ open, onClose, onFiled }) => {
  const [form, setForm] = useState({
    filer_id: '', filer_type: 'guest', target_id: '', target_type: 'hotel',
    hotel_id: '', category: 'safety_violation', description: '',
    evidence: '', stake_amount: 100,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.filer_id || !form.target_id || !form.hotel_id || !form.description) {
      toast.error('Fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const evidence = form.evidence.trim() ? form.evidence.split('\n').filter(Boolean) : [];
      await fileClaim({ ...form, evidence, stake_amount: Number(form.stake_amount) });
      toast.success('Claim filed successfully');
      onFiled();
      onClose();
    } catch { toast.error('Failed to file claim'); }
    finally { setSubmitting(false); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="file-claim-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">File Whistleblower Claim</DialogTitle>
          <DialogDescription>Asymmetric stake: you risk 1.5x your stake if the claim is dismissed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Your ID</Label>
              <Input placeholder="guest-101" value={form.filer_id} onChange={e => set('filer_id', e.target.value)} data-testid="input-filer-id" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Your Type</Label>
              <Select value={form.filer_type} onValueChange={v => set('filer_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['guest', 'staff', 'manager', 'external'].map(t => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Target ID</Label>
              <Input placeholder="hotel-001" value={form.target_id} onChange={e => set('target_id', e.target.value)} data-testid="input-target-id" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Target Type</Label>
              <Select value={form.target_type} onValueChange={v => set('target_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['hotel', 'staff', 'guest', 'agent'].map(t => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Hotel ID</Label>
              <Input placeholder="grand-marina" value={form.hotel_id} onChange={e => set('hotel_id', e.target.value)} data-testid="input-claim-hotel" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={v => set('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryLabels).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Description</Label>
            <Textarea placeholder="Describe the violation..." value={form.description} onChange={e => set('description', e.target.value)} rows={3} data-testid="input-description" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Evidence (one per line)</Label>
            <Textarea placeholder="Photo URL, witness statement..." value={form.evidence} onChange={e => set('evidence', e.target.value)} rows={2} data-testid="input-evidence" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Stake Amount (HERMES)</Label>
            <Input type="number" min="0" value={form.stake_amount} onChange={e => set('stake_amount', e.target.value)} data-testid="input-stake" />
            <p className="text-[10px] text-amber-500">Risk if dismissed: {fmt(Math.floor(Number(form.stake_amount) * 1.5))} HERMES (1.5x)</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-claim-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'File Claim'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ───
export default function WhistleblowerPage() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [filterHotel, setFilterHotel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [showFileClaim, setShowFileClaim] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterHotel) params.hotel_id = filterHotel;
      if (filterStatus) params.status = filterStatus;
      const data = await getClaims(params);
      setClaims(data.claims || []);
    } catch { toast.error('Failed to load claims'); }
    finally { setLoading(false); }
  }, [filterHotel, filterStatus]);

  useEffect(() => { loadClaims(); }, [loadClaims]);

  const viewClaim = async (claimId) => {
    try {
      const detail = await getClaimDetail(claimId);
      setSelectedDetail(detail);
      setShowDetail(true);
    } catch { toast.error('Failed to load claim details'); }
  };

  const pendingCount = claims.filter(c => c.status === 'pending' || c.status === 'arbitration_pending').length;
  const resolvedCount = claims.filter(c => c.status.startsWith('resolved_')).length;

  return (
    <div className="admin-theme min-h-dvh bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="page-title">Justice Portal</h1>
              <p className="text-xs text-muted-foreground">Whistleblower Claims & Arbitration</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowFileClaim(true)} data-testid="file-claim-btn">
              <Plus className="h-4 w-4 mr-1" /> File Claim
            </Button>
            <Button variant="outline" size="sm" onClick={loadClaims} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="justice-kpis">
          {[
            { title: 'Total Claims', value: claims.length, icon: FileText, color: 'text-[hsl(var(--web3-primary))]' },
            { title: 'Pending/Active', value: pendingCount, icon: Clock, color: 'text-amber-500' },
            { title: 'Resolved', value: resolvedCount, icon: Gavel, color: 'text-emerald-500' },
          ].map(({ title, value, icon: Icon, color }) => (
            <Card key={title} className="card-hover-lift">
              <CardContent className="pt-5 pb-4 px-5 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
                  <p className="text-3xl heading-font tabular-nums font-semibold">{value}</p>
                </div>
                <Icon className={`h-6 w-6 ${color}`} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3" data-testid="claim-filters">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground uppercase">Filters:</span>
          </div>
          <Input
            placeholder="Hotel ID"
            value={filterHotel}
            onChange={(e) => setFilterHotel(e.target.value)}
            className="w-48 h-8 text-xs"
            data-testid="filter-hotel"
          />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="filter-status">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="arbitration_pending">Awaiting Arbitration</SelectItem>
              <SelectItem value="arbitration_active">In Arbitration</SelectItem>
              <SelectItem value="resolved_upheld">Upheld</SelectItem>
              <SelectItem value="resolved_dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={loadClaims} className="h-8 text-xs" data-testid="apply-filters-btn">Apply</Button>
        </div>

        {/* Claims List */}
        <Card data-testid="claims-list-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Scale className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Claims
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>
            ) : (
              <ScrollArea className="h-[400px]">
                {claims.length === 0 ? (
                  <div className="text-center py-16">
                    <Shield className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No claims filed yet</p>
                    <p className="text-xs text-muted-foreground">The protocol is clean — or nobody has spoken up yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {claims.map((c) => (
                      <ClaimCard key={c.claim_id} claim={c} onView={() => viewClaim(c.claim_id)} />
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      <FileClaimDialog open={showFileClaim} onClose={() => setShowFileClaim(false)} onFiled={loadClaims} />
      <ClaimDetailDialog open={showDetail} onClose={() => setShowDetail(false)} detail={selectedDetail} />
    </div>
  );
}
